import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import BackButton from '../components/BackButton';
import type { CasePriority } from '../types';

// Area values are persisted to the DB as Hebrew strings (matches the mockup
// and the existing rows). Keep the canonical value Hebrew; the label shown
// to the user comes from `cases.areas.*` and falls back to the value itself
// when no translation matches (e.g. legacy data).
const LEGAL_AREAS: { value: string; key: string }[] = [
  { value: 'דיני משפחה', key: 'family' },
  { value: 'נדל״ן', key: 'real_estate' },
  { value: 'דיני עבודה', key: 'labor' },
  { value: 'מסחרי', key: 'commercial' },
  { value: 'פלילי', key: 'criminal' },
  { value: 'ירושה', key: 'inheritance' },
  { value: 'נזיקין', key: 'tort' },
  { value: 'אחר', key: 'other' },
];

const FEE_TYPES = ['hourly', 'fixed', 'contingency'] as const;
const PRIORITIES: CasePriority[] = ['low', 'medium', 'high', 'urgent'];

export function AddCase({
  clientId,
  clientName,
  onSaved,
  onCancel,
}: {
  clientId: string;
  clientName: string;
  onSaved: (caseId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const [area, setArea] = useState('');
  const [feeType, setFeeType] = useState<'hourly' | 'fixed' | 'contingency'>('hourly');
  const [hourlyRate, setHourlyRate] = useState('');
  const [procedureType, setProcedureType] = useState('');
  const [court, setCourt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState<CasePriority>('medium');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await apiFetch<{ id: string }>('/api/cases', {
        method: 'POST',
        body: JSON.stringify({
          clientId,
          area: area || null,
          feeType,
          hourlyRateIls: hourlyRate ? Number(hourlyRate) : null,
          procedureType: procedureType.trim() || null,
          court: court.trim() || null,
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          priority,
          description: description.trim() || null,
        }),
      });
      onSaved(created.id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code
          : err instanceof Error
            ? err.message
            : t('common:errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="form-wrap">
      <BackButton onClick={onCancel} />
      <form className="form-card" onSubmit={handleSubmit}>
        <div className="form-card-header">
          <div>
            <div className="form-preview-name">{t('form.title_new')}</div>
            <div className="form-preview-type">{t('form.for_client', { clientName })}</div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_details')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('form.field_area')}</label>
              <select value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">{t('common:actions.select')}</option>
                {LEGAL_AREAS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {t(`areas.${a.key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('form.field_procedure_type')}</label>
              <input
                value={procedureType}
                onChange={(e) => setProcedureType(e.target.value)}
                placeholder={t('form.field_procedure_type_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('form.field_court')}</label>
              <input
                value={court}
                onChange={(e) => setCourt(e.target.value)}
                placeholder={t('form.field_court_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('form.field_priority')}</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as CasePriority)}>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`priorities.${p}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('form.field_due_at')}</label>
              <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('form.field_fee_type')}</label>
              <select
                value={feeType}
                onChange={(e) =>
                  setFeeType(e.target.value as 'hourly' | 'fixed' | 'contingency')
                }
              >
                {FEE_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {t(`fee_types.${f}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('form.field_hourly_rate')}</label>
              <input
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder={t('form.field_hourly_rate_placeholder')}
                min={0}
                step={50}
              />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>{t('form.field_description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder={t('form.field_description_placeholder')}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="form-section">
            <div className="signin-error">{error}</div>
          </div>
        )}

        <div className="form-footer">
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? t('common:actions.loading_dots') : t('form.submit')}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>
            {t('common:actions.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
